import InventoryStore from 'admin/InventoryStore'
import { AlertFactory } from 'alerts/Alert'
import Workspace from 'iam/models/Workspace'
import {
  FilterItem,
  FilterItemSource,
  FilterStorage,
  FiltersViewModel,
} from 'infra/FiltersViewModel'
import { action, computed, makeObservable, observable, reaction } from 'mobx'
import { Subscription, combineLatest } from 'rxjs'
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators'
import { Id } from 'types/definitions'
import { shareLatest } from 'utils/rx-util'
import { SingleSelectFilter } from '../../infra/FilterControls/SingleSelect/SingleSelectFilter'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import InventoryItem from './InventoryItem'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import InventoryItemRequestQueryFilterBuilder, {
  InventoryItemArchivedFilterOptions,
  InventoryItemOverrideFilterOptions,
  InventoryItemProVisibilityFilterOptions,
} from './InventoryItemRequestQueryFilterBuilder'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'

export type RefreshInventoryItems = (workspace: Workspace) => Promise<void>

/**
 * Possible values to filter by Item Type.
 */
export enum InventoryItemTypeFilterOption {
  Active,
  Overridden,
  Hidden,
  Archived,
}

export interface InventoryItemTypeFilter {
  title: string
  type: InventoryItemTypeFilterOption
  visible: boolean
}

export const InventoryItemTypeFilter = {
  none: {
    title: '',
    type: InventoryItemTypeFilterOption.Active,
    visible: false,
  } as InventoryItemTypeFilter,
}

export type InventoryItemFilter = {
  inventoryItemType: InventoryItemTypeFilter
  workspace: Workspace | undefined
}

export default class InventorySearchViewModel {
  /**
   * Observable subscription to workspace$ used for disposing on deactivate.
   */
  private subscription = new Subscription()

  /**
   * Search text.
   */
  searchText = new SearchTextViewModel()

  @observable
  archivedItemsFilter: InventoryItemArchivedFilterOptions =
    InventoryItemArchivedFilterOptions.ACTIVE

  @observable
  proVisibilityItemsFilter: InventoryItemProVisibilityFilterOptions =
    InventoryItemProVisibilityFilterOptions.ALL

  @observable
  overriddenItemsFilter: InventoryItemOverrideFilterOptions =
    InventoryItemOverrideFilterOptions.All

  /**
   * Inventory filters
   */
  filters = new FiltersViewModel<InventoryItemFilter>({
    filterStorage: this.filterStorage,
    definitions: {
      inventoryItemType: {
        icon: 'pending',
        title: 'View:',
        implicit: true,
        factory: (key, title) =>
          new SingleSelectFilter(
            title,
            key,
            InventoryItemTypeFilter.none,
            this.getInventoryItemTypeOptions.bind(this)
          ),
      },
      workspace: {
        icon: 'pending',
        title: 'View:',
        implicit: true,
        factory: (key, title) =>
          new SingleSelectFilter(
            title,
            key,
            undefined,
            // The workspace filter options are managed externally from the
            // filter view model.
            FilterItemSource.static([])
          ),
      },
    },
  })

  @computed
  get canManageGlobalInventory() {
    return this.getWorkspaceMemberships().some((wms) =>
      wms.member.hasPermission('ADMIN_MANAGE_GLOBAL_INVENTORY_ITEMS')
    )
  }

  /**
   * Server query pipeline.
   */
  query$ = combineLatest([
    this.filters.appliedFilters$.pipe(
      map((result) => result.workspace?.id),
      distinctUntilChanged(),
      tap(() => this.inventoryTypeFilter.activate())
    ),
    this.searchText.debouncedValue$,
  ]).pipe(
    switchMap(() => this.scrollProvider.fetch()),
    shareLatest()
  )

  constructor(
    private newAlert: AlertFactory,
    private inventoryStore: InventoryStore,
    private getWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private filterStorage?: FilterStorage
  ) {
    makeObservable(this)

    reaction(
      () => this.filters.appliedFilters.inventoryItemType?.type,
      (inventoryItemFilter) => {
        this.updateFilterItems(inventoryItemFilter)
      }
    )

    reaction(
      () => this.filters.appliedFilters.workspace?.id,
      () => {
        this.searchText.clear()
        this.inventoryTypeFilter.clearValue()
        this.updateFilterItems(this.inventoryTypeFilter.value.type)
      }
    )
  }

  @computed
  get serviceItems() {
    return this.scrollProvider.items.slice()
  }

  @computed
  get getTotalItems() {
    if (!this.workspace?.id) {
      return this.inventoryStore.totalItemCountPerWorkspace.get('global')
    }
    return this.inventoryStore.totalItemCountPerWorkspace.get(this.workspace.id)
  }

  /**
   * Workspace filter
   */
  @computed
  get workspaceFilter() {
    return this.filters.getFilter('workspace') as SingleSelectFilter<
      string,
      Workspace | undefined
    >
  }

  /**
   * Inventory type filter
   */
  @computed
  get inventoryTypeFilter() {
    return this.filters.getFilter('inventoryItemType') as SingleSelectFilter<
      string,
      InventoryItemTypeFilter
    >
  }

  /**
   * Search text computed getter.
   */
  @computed
  get workspace() {
    return this.workspaceFilter.value
  }

  /**
   * Whether we have the Global Inventory option selected or not.
   */
  @computed
  get isGlobalInventorySelected() {
    return this.workspaceFilter.value === undefined
  }

  private async _fetchInventoryItems(
    workspaceId: Id,
    limit: number,
    archivedItemsFilter: InventoryItemArchivedFilterOptions,
    proVisibilityItemsFilter: InventoryItemProVisibilityFilterOptions,
    overriddenItemsFilter: InventoryItemOverrideFilterOptions,
    pageToken?: string,
    searchText?: string
  ) {
    if (this.canManageGlobalInventory || workspaceId) {
      return await this.inventoryStore.fetchInventoryItems(
        workspaceId,
        limit,
        searchText,
        pageToken,
        new InventoryItemRequestQueryFilterBuilder()
          .withArchivedStatus(archivedItemsFilter)
          .withProVisibilityStatus(proVisibilityItemsFilter)
          .withOverrideStatus(overriddenItemsFilter)
          .build()
      )
    }
    return {
      data: [],
      total: 0,
      cursor: '',
    }
  }

  scrollProvider = new InfiniteScrollProvider<InventoryItem>({
    limit: 200,
    startFetchTaskAsPending: true,
    fetchItems: (paging) =>
      this._fetchInventoryItems(
        this.workspace?.id,
        paging.limit ?? 200,
        this.archivedItemsFilter,
        this.proVisibilityItemsFilter,
        this.overriddenItemsFilter,
        paging.after ?? undefined,
        this.searchText.value
      ),
  })

  /**
   * Options for the inventory item type filter.
   */
  async getInventoryItemTypeOptions(): Promise<
    FilterItem<InventoryItemTypeFilter>[]
  > {
    return [
      {
        title: 'Active items',
        value: {
          title: 'Active items',
          type: InventoryItemTypeFilterOption.Active,
          visible: true,
        },
      },
      {
        title: 'Overridden items',
        value: {
          title: 'Overridden items',
          type: InventoryItemTypeFilterOption.Overridden,
          visible: !this.isGlobalInventorySelected,
        },
      },
      {
        title: 'Hidden items',
        value: {
          title: 'Hidden items',
          type: InventoryItemTypeFilterOption.Hidden,
          visible: true,
        },
      },
      {
        title: 'Archived items',
        value: {
          title: 'Archived items',
          type: InventoryItemTypeFilterOption.Archived,
          visible: true,
        },
      },
    ]
  }

  /**
   * Sets the archived items filter.
   *
   * @param value
   */
  @action.bound
  setArchivedItemsFilter(value: InventoryItemArchivedFilterOptions) {
    this.archivedItemsFilter = value
  }

  /**
   * Sets the pro visibility items filter.
   *
   * @param value
   */
  @action.bound
  setProVisibilityItemsFilter(value: InventoryItemProVisibilityFilterOptions) {
    this.proVisibilityItemsFilter = value
  }

  /**
   * Sets the overridden items filter.
   *
   * @param value
   */
  @action.bound
  setOverriddenItemsFilter(value: InventoryItemOverrideFilterOptions) {
    this.overriddenItemsFilter = value
  }

  /**
   * Sets the search text.
   *
   * @param value
   */
  @action.bound
  setWorkspace(value?: Workspace) {
    this.workspaceFilter.change(value)
  }

  async refreshItems() {
    this.inventoryStore.inventoryItems.clear()
    this.scrollProvider.fetch()
  }

  async activate() {
    this.subscription = this.query$.subscribe()
    // Activate the filters
    this.filters.activate()
  }

  /**
   * Updates the filter items based on the selected filter.
   */
  updateFilterItems(filter?: InventoryItemTypeFilterOption) {
    if (filter === undefined) {
      return
    }

    switch (filter) {
      case InventoryItemTypeFilterOption.Active:
        this.setArchivedItemsFilter(InventoryItemArchivedFilterOptions.ACTIVE)
        this.setProVisibilityItemsFilter(
          InventoryItemProVisibilityFilterOptions.ALL
        )
        this.setOverriddenItemsFilter(InventoryItemOverrideFilterOptions.All)
        this.refreshItems()
        break
      case InventoryItemTypeFilterOption.Overridden:
        this.setArchivedItemsFilter(InventoryItemArchivedFilterOptions.ACTIVE)
        this.setProVisibilityItemsFilter(
          InventoryItemProVisibilityFilterOptions.ALL
        )
        this.setOverriddenItemsFilter(InventoryItemOverrideFilterOptions.ONLY)
        this.refreshItems()
        break
      case InventoryItemTypeFilterOption.Hidden:
        this.setArchivedItemsFilter(InventoryItemArchivedFilterOptions.ACTIVE)
        this.setProVisibilityItemsFilter(
          InventoryItemProVisibilityFilterOptions.HIDDEN
        )
        this.setOverriddenItemsFilter(InventoryItemOverrideFilterOptions.All)
        this.refreshItems()
        break
      case InventoryItemTypeFilterOption.Archived:
        this.setArchivedItemsFilter(InventoryItemArchivedFilterOptions.ARCHIVED)
        this.setProVisibilityItemsFilter(
          InventoryItemProVisibilityFilterOptions.ALL
        )
        this.setOverriddenItemsFilter(InventoryItemOverrideFilterOptions.All)
        this.refreshItems()
        break
    }
  }

  /**
   * Called when the view model is not being actively used.
   */
  deactivate() {
    this.subscription.unsubscribe()
    this.filters.deactivate()
  }
}
